import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, LogOut, Printer, Sparkles, Layers, MapPin, Compass } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ModuleAccent from "@/components/ModuleAccent";

const EASE = [0.16, 1, 0.3, 1];
const BACKEND = "";

const ICON_MAP = {
    Printer:  Printer,
    Sparkles: Sparkles,
    Layers:   Layers,
    MapPin:   MapPin,
};

export default function Workspace() {
    const { user, loading: authLoading, logout } = useAuth();
    const navigate = useNavigate();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(null);

    useEffect(() => {
        if (authLoading) return;               // wait for AuthContext to resolve
        if (!user) {
            navigate("/login?next=/app");
            return;
        }
        (async () => {
            try {
                const res = await fetch(`${BACKEND}/api/users/me/apps`, { credentials: "include" });
                if (!res.ok) throw new Error("unauthorized");
                const data = await res.json();
                setApps(data.apps || []);
            } catch (e) {
                navigate("/login?next=/app");
            } finally {
                setLoading(false);
            }
        })();
    }, [user, authLoading, navigate]);

    const activate = async (appKey) => {
        setActivating(appKey);
        try {
            const res = await fetch(`${BACKEND}/api/users/me/activate/${appKey}`, {
                method: "POST",
                credentials: "include",
            });
            if (!res.ok) throw new Error("activation failed");
            // refresh apps
            const r2 = await fetch(`${BACKEND}/api/users/me/apps`, { credentials: "include" });
            const d2 = await r2.json();
            setApps(d2.apps || []);
        } catch (e) {
            alert("Non sono riuscito ad attivare l'app. Riprova.");
        } finally {
            setActivating(null);
        }
    };

    const firstName = (user?.name || user?.email || "").split(" ")[0] || "";

    return (
        <ModuleAccent
            module="workspace"
            as="div"
            className="min-h-screen w-full"
            style={{
                background: "#05050F",
                fontFamily: "Geist, Outfit, sans-serif",
                color: "#fff",
            }}
            data-testid="workspace-page"
        >
            {/* Soft blue ambient */}
            <div aria-hidden style={{
                position: "fixed", inset: 0, pointerEvents: "none",
                background: "radial-gradient(circle at 20% 10%, rgba(79,139,255,0.16) 0%, rgba(5,5,15,0) 50%), radial-gradient(circle at 80% 80%, rgba(0,229,199,0.10) 0%, rgba(5,5,15,0) 50%)",
            }} />

            {/* Top bar */}
            <header className="relative z-10 px-6 md:px-10 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center group" data-testid="workspace-home-link" aria-label="TotalKlick home">
                    <img
                        src="/brand/totalklick-logo.svg"
                        alt="TotalKlick"
                        className="h-7 w-auto transition-transform group-hover:scale-[1.03]"
                        style={{ filter: "drop-shadow(0 0 14px rgba(79,139,255,0.4))" }}
                    />
                </Link>
                <div className="flex items-center gap-3">
                    <span className="hidden sm:inline text-[12.5px] text-white/55">{user?.email}</span>
                    {user?.role === "superadmin" && (
                        <button
                            type="button"
                            onClick={() => navigate("/superadmin")}
                            className="text-[12.5px] text-white border px-3.5 py-1.5 rounded-full transition flex items-center gap-1.5 hover:opacity-90"
                            style={{
                                background: "linear-gradient(135deg, rgba(139,92,246,0.25), rgba(34,211,238,0.15))",
                                borderColor: "rgba(139,92,246,0.45)",
                                boxShadow: "0 8px 20px -8px rgba(139,92,246,0.5)",
                            }}
                            data-testid="workspace-mission-control-btn"
                            title="Torna al Mission Control"
                        >
                            <Compass size={13} /> Mission Control
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => { logout(); navigate("/"); }}
                        className="text-[12.5px] text-white/70 hover:text-white border border-white/12 px-3.5 py-1.5 rounded-full hover:bg-white/5 transition flex items-center gap-1.5"
                        data-testid="workspace-logout"
                    >
                        <LogOut size={13} /> Esci
                    </button>
                </div>
            </header>

            {/* Main content */}
            <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                >
                    <div className="text-[10px] tracking-[0.3em] uppercase text-blue-300/65 mb-3">Workspace</div>
                    <h1
                        className="text-white mb-3"
                        style={{
                            fontSize: "clamp(2rem, 5vw, 3.4rem)",
                            fontWeight: 500,
                            letterSpacing: "-0.03em",
                            lineHeight: 1.02,
                        }}
                    >
                        Bentornato{firstName ? `, ${firstName}` : ""}.
                    </h1>
                    <p
                        className="text-white/55 max-w-xl"
                        style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(1rem, 1.5vw, 1.1rem)", lineHeight: 1.55 }}
                    >
                        Ecco le tue app TotalKlick. Entra in quelle attive o attivane di nuove con un click.
                    </p>
                </motion.div>

                {loading ? (
                    <div className="mt-16 text-white/45 text-[13px]">Caricamento…</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-5 mt-10 md:mt-14">
                        {apps.map((app, i) => {
                            const Icon = ICON_MAP[app.icon] || Sparkles;
                            const external = app.url.startsWith("http");
                            const Wrapper = app.activated
                                ? (external ? "a" : Link)
                                : "div";
                            const wrapperProps = app.activated
                                ? (external
                                    ? { href: app.url, target: "_blank", rel: "noopener noreferrer" }
                                    : { to: app.url })
                                : {};
                            return (
                                <motion.div
                                    key={app.app_key}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, ease: EASE, delay: 0.15 + i * 0.08 }}
                                >
                                    <Wrapper
                                        {...wrapperProps}
                                        className={`group relative block rounded-2xl p-5 md:p-6 border transition ${app.activated ? "hover:scale-[1.01] cursor-pointer" : ""}`}
                                        style={{
                                            background: "rgba(15,18,28,0.65)",
                                            borderColor: app.activated ? `${app.color}30` : "rgba(255,255,255,0.06)",
                                            backdropFilter: "blur(20px)",
                                            WebkitBackdropFilter: "blur(20px)",
                                            boxShadow: app.activated ? `0 20px 50px -18px ${app.color}40` : "none",
                                        }}
                                        data-testid={`workspace-card-${app.app_key}`}
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <div
                                                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                                                style={{
                                                    background: `linear-gradient(135deg, ${app.color}30 0%, ${app.color}10 100%)`,
                                                    border: `1px solid ${app.color}40`,
                                                }}
                                            >
                                                <Icon size={20} style={{ color: app.color }} />
                                            </div>
                                            {app.activated ? (
                                                <span
                                                    className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full"
                                                    style={{
                                                        background: `${app.color}18`,
                                                        color: app.color,
                                                        border: `1px solid ${app.color}40`,
                                                    }}
                                                >
                                                    {app.plan || "Attiva"}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full text-white/50 border border-white/12">
                                                    Non attiva
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-white text-[18px] mb-1" style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>
                                            {app.name}
                                        </div>
                                        <div className="text-white/55 text-[13px] mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
                                            {app.tagline}
                                        </div>

                                        {app.activated ? (
                                            <div className="flex items-center justify-between">
                                                <div className="text-[11px] text-white/40" style={{ fontFamily: "Geist, sans-serif" }}>
                                                    {app.last_login
                                                        ? `Ultimo accesso · ${new Date(app.last_login).toLocaleDateString("it-IT")}`
                                                        : "Mai usata"}
                                                </div>
                                                <span
                                                    className="inline-flex items-center gap-1.5 text-[12.5px] transition group-hover:translate-x-0.5"
                                                    style={{ color: app.color }}
                                                >
                                                    Apri <ArrowRight size={14} />
                                                </span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); activate(app.app_key); }}
                                                disabled={activating === app.app_key}
                                                className="inline-flex items-center gap-1.5 text-[12.5px] text-white/85 hover:text-white border border-white/15 px-3.5 py-1.5 rounded-full transition disabled:opacity-50"
                                                data-testid={`workspace-activate-${app.app_key}`}
                                            >
                                                <Plus size={13} />
                                                {activating === app.app_key ? "Attivazione…" : "Attiva"}
                                            </button>
                                        )}
                                    </Wrapper>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Bottom helpful links */}
                <div className="mt-14 md:mt-20 flex flex-wrap items-center gap-4 text-[12.5px] text-white/55" style={{ fontFamily: "Geist, sans-serif" }}>
                    <Link to="/manuale/cliente" className="hover:text-white transition" data-testid="workspace-link-manual">Manuale</Link>
                    <span className="text-white/15">·</span>
                    <Link to="/" className="hover:text-white transition" data-testid="workspace-link-hub">Esplora ecosistema</Link>
                    <span className="text-white/15">·</span>
                    <span className="text-white/35">© {new Date().getFullYear()} TotalKlick</span>
                </div>
            </main>
        </ModuleAccent>
    );
}
